/**
 * Helper functins
 *  Map --> Helper to update or add a source + layer
 */
import { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import "./TrafficMonitoringMap.css";
import config from "../../../../../config";
import { getEventIcon } from "modules/eventIcons";

mapboxgl.accessToken = config.MAPBOX_ACCESS_TOKEN_3D;

import { Checkbox, FormControlLabel, Switch } from "@mui/material";
import dayjs from "dayjs";

// Normalize category.name to be icon-safe
const normalizeIconName = (name) =>
  name?.toLowerCase().replace(/\s+/g, "-").trim();

const safeParse = (val) => {
  try {
    return typeof val === "string" ? JSON.parse(val) : val;
  } catch {
    return val;
  }
};

const getPopupCoordinates = (feature) => {
  if (!feature || !feature.geometry) return null;

  const { geometry } = feature;

  if (geometry.type === "Point" && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates.slice(); // defensive copy
  }

  if (
    geometry.type === "LineString" &&
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length > 0
  ) {
    const coordsArray = geometry.coordinates;
    const midIndex = Math.floor(coordsArray.length / 2);
    return coordsArray[midIndex];
  }

  return null;
};

const setColorLine = (limit, avgSpeed) => {
  const rgbColor = {
    green: "#12B51A",
    orange: "#E98500",
    red: "#C74811",
    darkRed: "#970029",
    // green: "#E98500",
    // orange: "#C74811",
    // red: "#970029",
    // darkRed: "#4C0035",
  };

const thresholds = {
  20:  { green: 14, orange: 8,  red: 4 },
  25:  { green: 18, orange: 10, red: 5 },
  29:  { green: 20, orange: 12, red: 6 },
  30:  { green: 21, orange: 12, red: 6 },
  36:  { green: 25, orange: 14, red: 7 },
  37:  { green: 26, orange: 15, red: 7 },
  40:  { green: 28, orange: 16, red: 8 },
  50:  { green: 35, orange: 20, red: 10 },
  60:  { green: 42, orange: 24, red: 12 },
  70:  { green: 49, orange: 28, red: 14 },
  80:  { green: 56, orange: 32, red: 16 },
  85:  { green: 60, orange: 34, red: 17 },
  90:  { green: 63, orange: 36, red: 18 },
  100: { green: 70, orange: 40, red: 20 },
  110: { green: 77, orange: 44, red: 22 },
  120: { green: 84, orange: 48, red: 24 },
  130: { green: 91, orange: 52, red: 26 },
};

  const t = thresholds[limit];

  if (!t) return rgbColor.green; // default color if limit not found

  let selected;
  if (avgSpeed > t.green) {
    selected = "green";
  } else if (avgSpeed > t.orange) {
    selected = "orange";
  } else if (avgSpeed > t.red) {
    selected = "red";
  } else {
    selected = "darkRed";
  }

  return rgbColor[selected];
};

function formatMinuteDotSecond(value) {
  if (value == null || isNaN(value)) return "";

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  const [minutePart, secondPart] = absValue.toString().split(".");
  const totalMinutes = parseInt(minutePart, 10);
  const seconds = secondPart ? parseInt(secondPart.padEnd(2, "0"), 10) : 0;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let result = isNegative ? "-" : "";
  if (hours > 0) result += `${hours} hr `;
  if (minutes > 0 || hours > 0) result += `${minutes} min `;
  result += `${seconds} sec`;

  return result.trim();
}

const MAP_CENTER_LAT = config.MAP_CENTER_LAT;
const MAP_CENTER_LNG = config.MAP_CENTER_LNG;
const MAP_CENTER = [ MAP_CENTER_LAT,MAP_CENTER_LNG]
const MAP_ZOOM_LEVEL = config.MAP_ZOOM_LEVEL;
const map3D = config.MAP_3D;
const map2D = "mapbox://styles/mapbox/dark-v11";

function TrafficMonitoringMap({
  onlyActive: events,
  networkSource,
  areaRectSource,
}) {
  // console.log("TrafficMonitoringMap [onlyActive]: ", events);
  // console.log("TrafficMonitoringMap [networkSource]: ", networkSource);
  // console.log("TrafficMonitoringMap [areaRectSource]: ", areaRectSource);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const didMountRef = useRef(false);

  // State to manage checkboxes dynamically
  const [activeLayers, setActiveLayers] = useState({});
  const [dynamicLayers, setDynamicLayers] = useState([]);

  useEffect(() => {
    const layers = [];

    if (networkSource?.features?.length) {
      layers.push({ id: "network-layer", label: "Network" });
    }

    if (events?.length) {
      layers.push({ id: "events-group", label: "Events" });
    }

    if (areaRectSource?.features?.length) {
      layers.push({ id: "areaRect-layer", label: "Areas" });
    }

    setDynamicLayers(layers);
  }, [networkSource, events, areaRectSource]);

  useEffect(() => {
    if (!dynamicLayers.length) return;

    const defaultState = dynamicLayers.reduce((acc, layer) => {
      acc[layer.id] = true; // Default all to visible
      return acc;
    }, {});
    setActiveLayers(defaultState);
  }, [dynamicLayers]);

  const handleLayerToggle = (layerId) => {
    if (!mapRef.current) return;

    if (layerId === "events-group") {
      const visibility =
        mapRef.current.getLayoutProperty("events-point-layer", "visibility") ===
        "visible"
          ? "none"
          : "visible";

      [
        "events-point-layer",
        "events-lineString-layer",
        "events-lineStringSymbol-layer",
      ].forEach((id) => {
        if (mapRef.current.getLayer(id)) {
          mapRef.current.setLayoutProperty(id, "visibility", visibility);
        }
      });

      setActiveLayers((prev) => ({
        ...prev,
        [layerId]: visibility === "visible",
      }));
    } else {
      const visibility =
        mapRef.current.getLayoutProperty(layerId, "visibility") === "visible"
          ? "none"
          : "visible";

      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.setLayoutProperty(layerId, "visibility", visibility);
      }

      //Add this: if toggling area fill, also toggle the outline
      if (
        layerId === "areaRect-layer" &&
        mapRef.current.getLayer("areaRect-layer-outline")
      ) {
        mapRef.current.setLayoutProperty(
          "areaRect-layer-outline",
          "visibility",
          visibility
        );
      }

      setActiveLayers((prev) => ({
        ...prev,
        [layerId]: visibility === "visible",
      }));
    }
  };

  const [selectedMapStyle, setSelectedMapStyle] = useState(map3D);

  // Displayed Message
  const displayHoverMessage = (feature) => {
    const props = feature.properties;
    const category = safeParse(props.category);
    const subcategory = safeParse(props.subcategory);
    const source = safeParse(props.source);
    const type_of_event = safeParse(props.type_of_event);

    return `
      <div>
      ${category?.name ? `Category: ` : ""}
         ${category?.name || ""}${
      subcategory?.name ? ` (${subcategory.name})` : ""
    }<br />
        ${type_of_event?.name ? `Type: ${type_of_event.name}` : ""}<br/>
        ${source?.name ? `Source: (${source.name})` : ""}<br/>       
      </div>
    `;
  };

  const displayClickMessage = (feature) => {
    const props = feature.properties;
    const category = safeParse(props.category);
    const subcategory = safeParse(props.subcategory);
    const source = safeParse(props.source);
    const type_of_event = safeParse(props.type_of_event);

    return `
      <div class="custom-popup-content">
      ${category?.name ? `Category: ` : ""}
         ${category?.name || ""}${
      subcategory?.name ? ` (${subcategory.name})` : ""
    }<br />
        ${type_of_event?.name ? `Type: ${type_of_event.name}` : ""}<br/>
        ${source?.name ? `Source: (${source.name})` : ""}<br/>      
    `;
  };

  // set and reset Features
  const resetFeatureState = (map, source, id, stateKey) => {
    if (id !== null) {
      map.setFeatureState({ source, id }, { [stateKey]: false });
    }
  };

  const setFeatureState = (map, source, id, stateKey) => {
    map.setFeatureState({ source, id }, { [stateKey]: true });
  };

  const renderNetworkLayer = (map) => {
    if (!map || !networkSource?.features?.length) return;

    // 1. Build FeatureCollection with top-level string IDs
    const networkFeatures = networkSource.features.map((f) => ({
      type: "Feature",
      id: f.properties.id_path, // ✅ ID required for feature-state
      geometry: f.geometry,
      properties: {
        ...f.properties,
        colorLine: setColorLine(
          f.properties.speed_limit,
          f.properties.avg_speed_paths
        ),
      },
    }));

    // Helper to update or add a source + layer
    const updateOrAdd = (sourceId, layerId, layerConfig, data) => {
      const existingSource = map.getSource(sourceId);

      if (existingSource) {
        existingSource.setData(data);
      } else {
        map.addSource(sourceId, {
          type: "geojson",
          data,
        });

        if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            source: sourceId,
            ...layerConfig,
          });
        }
      }
    };

    // Add source & layer for linestring [network-source, network-layer]
    updateOrAdd(
      "network-source",
      "network-layer",
      {
        type: "line",
        slot: "middle",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-emissive-strength": 1.0,
          "line-color": ["get", "colorLine"],
          "line-width": [
            "case",
            // 1) if colorLine is exactly "#fff", use width 20
            ["==", ["get", "colorLine"], "#970029"],
            8,
            ["boolean", ["feature-state", "click"], false],
            8,
            4,
          ],
          "line-opacity": [
            "case",
            ["boolean", ["feature-state", "click"], false],
            0.4,
            ["boolean", ["feature-state", "hover"], false],
            0.5,
            1,
          ],
        },
      },
      {
        type: "FeatureCollection",
        features: networkFeatures,
      }
    );

    // 5. Hover Interactivity
    const hoverPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    });

    let hoveredNetworkId = null;

    map.on("mouseenter", "network-layer", (e) => {
      map.getCanvas().style.cursor = "pointer";
      const feature = e.features[0];
      if (!feature) return;

      const hoveredId = feature.id;
      const props = feature.properties;
      const delay = formatMinuteDotSecond(props.delay);
      const tt_paths = formatMinuteDotSecond(props.tt_paths);
      const date = dayjs(props.timestamp).format("DD/MM/YYYY HH:mm");

      resetFeatureState(map, "network-source", hoveredNetworkId, "hover");
      hoveredNetworkId = hoveredId;
      setFeatureState(map, "network-source", hoveredNetworkId, "hover");

      const content = `
        <div>
          
          <p><strong>Average Speed:</strong> ${props.avg_speed_paths} km/h</p>
          <p><strong>Delay:</strong> ${delay}</p>
          <p><strong>Travel Time:</strong> ${tt_paths}</p>
          
        </div>
      `;

      hoverPopup.setLngLat(e.lngLat).setHTML(content).addTo(map);
    });

    map.on("mouseleave", "network-layer", () => {
      map.getCanvas().style.cursor = "";
      hoverPopup.remove();

      if (hoveredNetworkId !== null) {
        resetFeatureState(map, "network-source", hoveredNetworkId, "hover");
        hoveredNetworkId = null;
      }
    });

    // 6. Click Interactivity
    const clickPopup = new mapboxgl.Popup({
      className: "my-custom-popup",
      closeButton: true,
      closeOnClick: true,
    });

    let clickedNetworkId = null;

    map.on("click", "network-layer", (e) => {
      const feature = e.features[0];
      if (!feature) return;

      const clickedId = feature.id;
      const props = feature.properties;
      const delay = formatMinuteDotSecond(props.delay);
      const tt_paths = formatMinuteDotSecond(props.tt_paths);
      const date = dayjs(props.timestamp).format("DD/MM/YYYY HH:mm");

      resetFeatureState(map, "network-source", clickedNetworkId, "click");
      clickedNetworkId = clickedId;
      setFeatureState(map, "network-source", clickedNetworkId, "click");

      const content = `
        <div class="custom-popup-content">
          
          <p><strong>Average Speed:</strong> ${props.avg_speed_paths} km/h</p>
          <p><strong>Delay:</strong> ${delay}</p>
          <p><strong>Travel Time:</strong> ${tt_paths}</p>
          
        </div>
      `;

      clickPopup.setLngLat(e.lngLat).setHTML(content).addTo(map);

      // Reset style when popup closes
      clickPopup.on("close", () => {
        if (clickedNetworkId !== null) {
          resetFeatureState(map, "network-source", clickedNetworkId, "click");
          clickedNetworkId = null;
        }
      });
    });
  };

  // Helper to render layers
  const renderEventLayers = (map) => {
    if (!map) return;

    // 2. Organize features
    const pointFeatures = [];
    const lineFeatures = [];
    const lineSymbolFeatures = [];

    const uniqueIcons = new Map();

    // 3. Create feature to pass on source based on geom
    events.forEach((event) => {
      const geom = event.geometry;
      if (!geom) return;

      const iconName = normalizeIconName(event.category?.name);
      const iconPath = getEventIcon(event.category?.id);

      // Exclude nested geometry from properties
      const { geometry, ...eventWithoutGeometry } = event;

      const feature = {
        id: event.id,
        type: "Feature",
        geometry: {
          type: geom.type,
          coordinates:
            geom.type === "Point" ? geom.coordinates[0] : geom.coordinates,
        },
        properties: {
          ...eventWithoutGeometry,
          icon: iconName,
        },
      };

      if (geom.type === "Point") {
        pointFeatures.push(feature);
        if (!uniqueIcons.has(iconName)) {
          uniqueIcons.set(iconName, iconPath);
        }
      } else if (geom.type === "LineString") {
        lineFeatures.push(feature);
        if (!uniqueIcons.has(iconName)) {
          uniqueIcons.set(iconName, iconPath);
        }
        // Create a symbol at the first coordinate of the line

        lineSymbolFeatures.push({
          id: `${event.id}`,
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: geom.coordinates[0],
          },
          properties: {
            ...eventWithoutGeometry,
            icon: iconName,
            isLine: true,
          },
        });
      }
    });

    // 4. Load unique icons
    uniqueIcons.forEach((path, name) => {
      if (!map.hasImage(name)) {
        map.loadImage(path, (err, image) => {
          if (err) return console.error("Error loading icon:", err);
          if (!map.hasImage(name)) {
            map.addImage(name, image);
          }
        });
      }
    });

    // Helper to update or add a source + layer
    const updateOrAdd = (sourceId, layerId, layerConfig, data) => {
      const existingSource = map.getSource(sourceId);

      if (existingSource) {
        existingSource.setData(data);
      } else {
        map.addSource(sourceId, {
          type: "geojson",
          data,
        });

        if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            source: sourceId,
            ...layerConfig,
          });
        }
      }
    };

    // 5. Update/Add sources and layers

    // 5a. Add source & layer for points [events-point, events-point-layer]
    updateOrAdd(
      "events-point",
      "events-point-layer",
      {
        type: "symbol",
        slot: "top",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": 0.9,
        },
        paint: {
          "icon-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            1.5,
            1,
          ],
        },
      },
      {
        type: "FeatureCollection",
        features: pointFeatures,
      }
    );

    // 5b. Add source & layer for linestring [events-lineString, events-lineString-layer]
    updateOrAdd(
      "events-lineString",
      "events-lineString-layer",
      {
        type: "line",
        slot: "top",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-emissive-strength": 1.0,
          "line-color": "#b33b32",
          "line-width": 1,
          "line-opacity": 0.5,
        },
      },
      {
        type: "FeatureCollection",
        features: lineFeatures,
      }
    );

    // 5c. Add source & layer for linestring-symbol [events-lineStringSymbol, events-lineStringSymbol-layer]
    updateOrAdd(
      "events-lineStringSymbol",
      "events-lineStringSymbol-layer",
      {
        type: "symbol",
        slot: "top",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": 0.9,
        },
      },
      {
        type: "FeatureCollection",
        features: lineSymbolFeatures,
      }
    );

    // 6. Add popup on hover for points and lines
    const hoverPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    });

    // Which layer to you hovering
    const hoverLayers = ["events-point-layer", "events-lineStringSymbol-layer"];

    // Track the hovered layer
    let hoveredPointId = null;
    let hoveredLineId = null;

    // 7/ Add hover popup
    hoverLayers.forEach((layerId) => {
      map.on("mouseenter", layerId, (e) => {
        map.getCanvas().style.cursor = "pointer";

        const feature = e.features[0];
        if (!feature) return;

        const coordinates = getPopupCoordinates(feature);
        if (!coordinates) return;

        const content = displayHoverMessage(feature);

        // Handle points
        if (layerId === "events-point-layer") {
          resetFeatureState(map, "events-point", hoveredPointId, "hover");
          hoveredPointId = feature.id;
          setFeatureState(map, "events-point", hoveredPointId, "hover");
        }

        // Handle line symbols
        if (layerId === "events-lineStringSymbol-layer") {
          resetFeatureState(
            map,
            "events-lineStringSymbol",
            hoveredLineId,
            "hover"
          );
          hoveredLineId = feature.id;
          setFeatureState(
            map,
            "events-lineStringSymbol",
            hoveredLineId,
            "hover"
          );
        }

        hoverPopup.setLngLat(coordinates).setHTML(content).addTo(map);
      }); // mouseenter

      map.on("mouseleave", layerId, () => {
        map.getCanvas().style.cursor = "";
        hoverPopup.remove();

        if (layerId === "events-point-layer" && hoveredPointId !== null) {
          resetFeatureState(map, "events-point", hoveredPointId, "hover");
          hoveredPointId = null;
        }

        if (
          layerId === "events-lineStringSymbol-layer" &&
          hoveredLineId !== null
        ) {
          resetFeatureState(
            map,
            "events-lineStringSymbol",
            hoveredLineId,
            "hover"
          );
          hoveredLineId = null;
        }
      }); // mouseleave
    }); // hover

    // 8. Add click popup
    const clickPopup = new mapboxgl.Popup({
      className: "my-custom-popup",
      closeButton: true,
      closeOnClick: true,
    });

    const clickLayers = ["events-point-layer", "events-lineStringSymbol-layer"];

    let clickedPointId = null;
    let clickedLineId = null;

    clickLayers.forEach((layerId) => {
      map.on("click", layerId, (e) => {
        const feature = e.features[0];
        if (!feature || !feature.id) return;

        const coordinates = getPopupCoordinates(feature);
        if (!coordinates) return;

        const content = displayClickMessage(feature);

        // Reset previous clicked states
        if (clickedPointId && layerId === "events-point-layer") {
          resetFeatureState(map, "events-point", clickedPointId, "click");
        }

        if (clickedLineId && layerId === "events-lineStringSymbol-layer") {
          resetFeatureState(
            map,
            "events-lineStringSymbol",
            clickedLineId,
            "click"
          );
        }

        // Set new clicked states
        if (layerId === "events-point-layer") {
          clickedPointId = feature.id;
          setFeatureState(map, "events-point", clickedPointId, "click");
        }

        if (layerId === "events-lineStringSymbol-layer") {
          clickedLineId = feature.id;
          setFeatureState(
            map,
            "events-lineStringSymbol",
            clickedLineId,
            "click"
          );
        }

        // Show popup
        clickPopup.setLngLat(coordinates).setHTML(content).addTo(map);
      });
    }); // click

    // }); // on load
  };

  // Helper to render areaRect
  const renderAreaRectLayer = (map) => {
    if (!areaRectSource?.features?.length) return;

    const geojsonData = areaRectSource;

    // Source
    if (!map.getSource("areaRect-source")) {
      map.addSource("areaRect-source", {
        type: "geojson",
        data: geojsonData,
      });
    } else {
      map.getSource("areaRect-source").setData(geojsonData);
    }

    // Layer
    if (!map.getLayer("areaRect-layer")) {
      map.addLayer({
        id: "areaRect-layer",
        type: "fill",
        source: "areaRect-source",
        paint: {
          "fill-emissive-strength": 1.0,
          "fill-color": "#666666",
          "fill-opacity": 0.5,
        },
      });

      map.addLayer({
        id: "areaRect-layer-outline",
        type: "line",
        source: "areaRect-source",
        paint: {
          "line-emissive-strength": 1.0,
          "line-color": "#000",
          "line-width": 2,
        },
      });

      // onHover("areaRect-layer");
    }
  };

  // 1. Initialize the map
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      // style: "mapbox://styles/mapbox/dark-v11",
      style: selectedMapStyle,
      projection: "mercator", // ← this makes transform.projection defined
      center: MAP_CENTER,
      zoom: MAP_ZOOM_LEVEL,
    });

    mapRef.current = map;
    map.once("load", () => {
      renderNetworkLayer(map);
      renderEventLayers(map);
      renderAreaRectLayer(map);
    });

    // Cleanup on unmount
    // return () => console.log("[INIT] Unmounting map") || map.remove();
    return () => map.remove();
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const doUpdate = () => {
      renderNetworkLayer(map);
      renderEventLayers(map);
      renderAreaRectLayer(map);
    };

    if (map.isStyleLoaded()) {
      // style is ready: update immediately
      doUpdate();
    } else {
      map.once("load", doUpdate);
    }
  }, [events, networkSource, areaRectSource]);

  // 3️⃣ REBUILD ON STYLE SWITCH
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    map.setStyle(selectedMapStyle);

    // after new style + tiles load, re-draw and restore visibilities
    map.once("styledata", (e) => {
      if (e.dataType !== "style") return;

      renderNetworkLayer(map);
      renderEventLayers(map);
      renderAreaRectLayer(map);
      Object.entries(activeLayers).forEach(([layerGroup, visible]) => {
        const vis = visible ? "visible" : "none";
        let ids;
        if (layerGroup === "events-group") {
          ids = [
            "events-point-layer",
            "events-lineString-layer",
            "events-lineStringSymbol-layer",
          ];
        } else if (layerGroup === "areaRect-layer") {
          ids = ["areaRect-layer", "areaRect-layer-outline"];
        } else {
          ids = [layerGroup];
        }
        ids.forEach((lid) => {
          if (map.getLayer(lid)) {
            map.setLayoutProperty(lid, "visibility", vis);
          }
        });
      });
    });
  }, [selectedMapStyle]);

  // 4️⃣ VISIBILITY-TOGGLE EFFECT
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) {
      return;
    }
    Object.entries(activeLayers).forEach(([layerGroup, visible]) => {
      const vis = visible ? "visible" : "none";
      let ids;
      if (layerGroup === "events-group") {
        ids = [
          "events-point-layer",
          "events-lineString-layer",
          "events-lineStringSymbol-layer",
        ];
      } else if (layerGroup === "areaRect-layer") {
        ids = ["areaRect-layer", "areaRect-layer-outline"];
      } else {
        ids = [layerGroup];
      }
      ids.forEach((lid) => {
        if (map.getLayer(lid)) {
          map.setLayoutProperty(lid, "visibility", vis);
        }
      });
    });
  }, [activeLayers]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          background: "#202940",
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "8px",
        }}
      >
        {/* Checkboxes to Toggle Layers */}
        {dynamicLayers.map((layer) => (
          <FormControlLabel
            key={layer.id}
            control={
              <Checkbox
                checked={!!activeLayers[layer.id]} // ✅ Use layer.id for checked state
                onChange={() => handleLayerToggle(layer.id)} // ✅ Toggle using id
              />
            }
            label={layer.label}
          />
        ))}
        {/* Toggle Switch */}
        <FormControlLabel
          control={
            <Switch
              className="custom-switch-3D"
              checked={selectedMapStyle === map3D}
              onChange={() =>
                setSelectedMapStyle((prev) => (prev === map3D ? map2D : map3D))
              }
            />
          }
          label={selectedMapStyle === map3D ? "3D ON" : "3D OFF"}
        />
      </div>

      <div
        id="trafficMonitoring-map"
        ref={mapContainerRef}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}

export default TrafficMonitoringMap;
